import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useTasksByFilterDocument } from 'features/tasks';
import React, { useMemo } from 'react';
import { useRegisterFullCalendarDraggableItems } from 'shared/hooks/use-register-full-calendar-draggable-items';
import { useUser } from 'shared/hooks/use-user';
import { FilterDocument } from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { DayPartGroupingTasksList } from './components/day-part-grouping-tasks-list';
import { DeadlineGroupingTasksList } from './components/deadline-grouping-tasks-list';
import { GoalGroupingTasksList } from './components/goal-grouping-tasks-list';
import { LifeAreaGroupingTasksList } from './components/life-area-grouping-tasks-list';
import { NoGroupingTasksList } from './components/no-grouping-tasks-list';
import { PriorityGroupingTasksList } from './components/priority-grouping-tasks-list';
import { TagGroupingTasksList } from './components/tag-grouping-tasks-list';
import { GroupedListProps } from './types';

export type TaskFilterListProps = {
  filterDocument: FilterDocument;
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  customOrder: Record<ID, ID[]>;
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  includeDuration?: boolean;
};

const groupByLists: Record<GroupByMode, React.FC<GroupedListProps>> = {
  [GroupByMode.None]: NoGroupingTasksList,
  [GroupByMode.LifeArea]: LifeAreaGroupingTasksList,
  [GroupByMode.Goal]: GoalGroupingTasksList,
  [GroupByMode.DayPart]: DayPartGroupingTasksList,
  [GroupByMode.Type]: NoGroupingTasksList,
  [GroupByMode.Priority]: PriorityGroupingTasksList,
  [GroupByMode.Deadline]: DeadlineGroupingTasksList,
  [GroupByMode.Tag]: TagGroupingTasksList,
};

export const TaskFilterList: React.FC<TaskFilterListProps> = ({
  filterDocument,
  onReorder,
  customOrder,
  onSectionCollapse,
  scrollElement,
  includeDuration,
}) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const goals = useSortedGoals();
  const tags = useSortedTags();

  // get tasks by filterDocument
  const tasks = useTasksByFilterDocument(filterDocument, {
    uncompletedOnly: true,
  });

  const collapsedSections = useMemo(
    () => filterDocument.collapsedSections ?? [],
    [filterDocument.collapsedSections],
  );

  useRegisterFullCalendarDraggableItems({
    listRef: scrollElement,
    tasks,
    weekStartsOn,
    includeDuration,
  });

  const GroupedList =
    groupByLists[filterDocument.groupByMode!] ?? groupByLists[GroupByMode.None];

  return (
    <GroupedList
      tasks={tasks}
      lifeAreas={lifeAreas}
      goals={goals}
      tags={tags}
      sortingMode={filterDocument.sortingMode}
      onReorder={onReorder}
      customOrder={customOrder}
      collapsedSections={collapsedSections}
      onSectionCollapse={onSectionCollapse}
      weekStartsOn={weekStartsOn}
      scrollElement={scrollElement}
    />
  );
};
